import { inject, LOCALE_ID, provideAppInitializer } from '@angular/core';
import { AppLang, DEFAULT_CONFIG, LocaleConfig, LocaleService } from './locale-service';
import { LocalizationService } from './localization.service';

const defaultConfig: LocaleConfig = {
  /** supported locale */
  locales: ['fr'],
  /** default locale */
  defaultLocale: 'fr',
  queryParam: undefined,
};

function resolveLocaleFromBrowser(appConfig: LocaleConfig): AppLang | undefined {
  const browserLang = window.navigator.language?.toLowerCase();
  return browserLang ? appConfig.locales.find(l => browserLang.startsWith(l)) : undefined;
}

function resolveLocaleFromQueryParam(appConfig: LocaleConfig): AppLang | undefined {
  if (appConfig.queryParam == null) {
    return undefined;
  }
  const queryParamLang = new URL(window.location.href)?.searchParams?.get(appConfig.queryParam);
  return queryParamLang ? appConfig.locales.find(l => l === queryParamLang) : undefined;
}

/**
 * Determine the effective language:
 * - Use user default if defined
 * - use browser locale: if it is supported by the application (it is registered in config.locales)
 */
function resolveLocaleConfig(config: Partial<LocaleConfig> | undefined): LocaleConfig {
  // default settings
  const appConfig: LocaleConfig = {
    ...defaultConfig,
    ...config,
  };

  // retrieves locale from query params
  const queryParamLocale = resolveLocaleFromQueryParam(appConfig);

  // retrieves browser locale
  const browserLocale = resolveLocaleFromBrowser(appConfig);

  // default locale will be the one from the browser (if supported) or the defaultLocale
  const defaultLocale = queryParamLocale ?? browserLocale ?? appConfig.defaultLocale;

  return {
    ...appConfig,
    defaultLocale,
    forcedLocale: queryParamLocale,
  };
}

/**
 * Initializes LocaleService and angular locale.
 *
 * Default locale is fr.
 */
export function provideLocale(config?: Partial<LocaleConfig>) {
  return [
    { provide: DEFAULT_CONFIG, useFactory: () => resolveLocaleConfig(config) },
    LocaleService,
    LocalizationService,
    provideAppInitializer(() => {
      const localeService = inject(LocaleService);
      return localeService.initLanguage();
    }),
    {
      provide: LOCALE_ID,
      useFactory: () => {
        const config = inject(DEFAULT_CONFIG);
        return LocaleService.getInitialLocale(config.defaultLocale, config.forcedLocale);
      },
    },
  ];
}
